import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useOutletContext } from "react-router";
import { Alert } from "@/components/alert";
import { Button } from "@/components/button";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Icon } from "@/components/icons";
import { useToast } from "@/components/action-feedback";
import type { AppOutletContext } from "@/src/layouts/AppLayout";
import { formatDate } from "@/app/(app)/_lib/format";
import {
  getCompanyConfig,
  getActiveDpaContract,
  getDpaSignature,
  enableDataRetention,
  submitContractSignature,
  disableDataRetention,
  updateDataRetentionPeriod,
  type DataRetentionStatus,
  type DataRetentionPeriod,
  type DpaSignature,
} from "@/src/services/company_service";
import { adaptApiInfo, getInfo } from "@/src/services/info_service";
import { MANAGER_ROLES, type AccountType } from "../_data";
import { RetentionPeriodPicker } from "./retention-period-picker";
import { SectionHeader } from "./section-header";
import { SignDpaModal } from "./sign-dpa-modal";
import { SkeletonCard } from "./skeleton-card";

type DataRetentionLoadStatus = "loading" | "error" | "ready";

const DATA_RETENTION_CHIP: Record<DataRetentionStatus, { className: string; label: string }> = {
  disabled: { className: "bg-grey-900 text-white", label: "Off" },
  requested: { className: "bg-yellow-100 text-yellow-700", label: "Pending" },
  enabled: { className: "bg-green-500-12 text-green-700", label: "On" },
};

function DataRetentionChip({ status }: { status: DataRetentionStatus }) {
  const c = DATA_RETENTION_CHIP[status];
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full px-4 py-1.5 text-small font-semibold ${c.className}`}
    >
      {c.label}
    </span>
  );
}

const DATA_RETENTION_HIGHLIGHTS: { icon: string; text: string }[] = [
  {
    icon: "history",
    text: "Search queries and results are stored so your team can revisit past checks and monitoring history.",
  },
  {
    icon: "lock",
    text: "Encrypted at rest, isolated to your company, never shared with third parties.",
  },
  {
    icon: "schedule",
    text: "Choose how long to keep it below. You can shorten this or turn it off anytime — turning it off clears stored history within an hour.",
  },
];

export function DataRetention() {
  const ctx = useOutletContext<AppOutletContext | null>();
  const insetLeft = ctx?.mainInsetLeft ?? 0;
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<DataRetentionLoadStatus>("loading");
  const [canManage, setCanManage] = useState(false);
  const [dataRetention, setDataRetention] = useState<DataRetentionStatus>("disabled");
  const [period, setPeriod] = useState<DataRetentionPeriod>("day");
  const [signerName, setSignerName] = useState("");
  const [signerEmail, setSignerEmail] = useState("");
  const [contractId, setContractId] = useState<string | null>(null);
  const [signature, setSignature] = useState<DpaSignature | null>(null);
  const [signModalOpen, setSignModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmDisableOpen, setConfirmDisableOpen] = useState(false);
  const [pendingPeriod, setPendingPeriod] = useState<DataRetentionPeriod | null>(null);
  const showToast = useToast();

  useEffect(() => {
    let cancelled = false;
    Promise.all([getCompanyConfig(), getInfo().then(adaptApiInfo)]).then(
      async ([configRes, info]) => {
        if (cancelled) return;
        if (!configRes.ok) {
          setStatus("error");
          return;
        }
        setDataRetention(configRes.value.dataRetention);
        setPeriod(
          configRes.value.dataRetention === "enabled" ? configRes.value.dataRetentionPeriod : "day",
        );
        const isManager = MANAGER_ROLES.includes(info.user.accountType as AccountType);
        setCanManage(isManager);
        setSignerName(info.user.name ?? "");
        setSignerEmail(info.user.email ?? "");

        // GET /contracts/ and GET /company/contracts/ are OWNER/ADMIN-only on the
        // backend — skip both for a non-manager rather than firing a call that's
        // just going to be rejected.
        const [contractRes, signatureRes] = isManager
          ? await Promise.all([getActiveDpaContract(), getDpaSignature()])
          : [null, null];
        if (cancelled) return;
        if (contractRes?.ok) setContractId(contractRes.value.id);
        if (signatureRes?.ok) setSignature(signatureRes.value);
        setStatus("ready");

        const shouldOpenSign = (location.state as { openSignDpa?: boolean } | null)?.openSignDpa;
        if (shouldOpenSign) {
          if (
            configRes.value.dataRetention === "disabled" &&
            contractRes?.ok &&
            !signatureRes?.ok
          ) {
            setSignModalOpen(true);
          }
          navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
        }
      },
      () => {
        if (!cancelled) setStatus("error");
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSigned(signed: { pdfBytes: Uint8Array; period: DataRetentionPeriod }) {
    setSignModalOpen(false);
    if (saving) return;
    if (!contractId) {
      setSaveError(
        "Couldn't find the current data retention agreement to sign. Please refresh and try again.",
      );
      return;
    }
    setSaving(true);
    setSaveError(null);
    const signedDocument = new Blob([new Uint8Array(signed.pdfBytes)], {
      type: "application/pdf",
    });
    const contractRes = await submitContractSignature(contractId, signedDocument);
    if (!contractRes.ok) {
      setSaving(false);
      setSaveError(contractRes.error.message);
      return;
    }

    const res = await enableDataRetention(signed.period);
    setSaving(false);
    if (!res.ok) {
      setSaveError(res.error.message);
      return;
    }
    setDataRetention(res.value.dataRetention);
    setPeriod(signed.period);
    setSignature(contractRes.value);
    showToast({ tone: "success", text: "Data retention has been enabled." });
  }
  async function handleReEnable() {
    setSaving(true);
    setSaveError(null);
    const res = await enableDataRetention(period);
    setSaving(false);
    if (!res.ok) {
      setSaveError(res.error.message);
      return;
    }
    setDataRetention(res.value.dataRetention);
    showToast({ tone: "success", text: "Data retention has been enabled." });
  }

  async function handleCancelRequest() {
    setSaving(true);
    setSaveError(null);
    const res = await disableDataRetention();
    setSaving(false);
    if (!res.ok) {
      setSaveError(res.error.message);
      return;
    }
    setDataRetention(res.value.dataRetention);
    setPeriod("day");
    showToast({ tone: "success", text: "Your data retention request has been cancelled." });
  }

  async function handleDisable() {
    setSaving(true);
    setSaveError(null);
    const res = await disableDataRetention();
    setSaving(false);
    setConfirmDisableOpen(false);
    if (!res.ok) {
      setSaveError(res.error.message);
      return;
    }
    setDataRetention(res.value.dataRetention);
    setPeriod("day");
    showToast({ tone: "success", text: "Data retention has been disabled." });
  }

  function handlePeriodChange(next: DataRetentionPeriod) {
    if (next === period) return;
    if (dataRetention !== "enabled") {
      setPeriod(next);
      return;
    }
    setSaveError(null);
    setPendingPeriod(next);
  }

  async function confirmPeriodChange() {
    const next = pendingPeriod;
    if (!next) return;
    setSaving(true);
    setSaveError(null);
    const res = await updateDataRetentionPeriod(next);
    setSaving(false);
    if (!res.ok) {
      setSaveError(res.error.message);
      setPendingPeriod(null);
      return;
    }
    setPeriod(next);
    setPendingPeriod(null);
    showToast({ tone: "success", text: "Retention period updated." });
  }

  if (status === "loading") return <SkeletonCard heightClass="h-[420px]" />;

  return (
    <div className="card @container flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4 border-b border-grey-200 pb-4">
        <SectionHeader
          title="Data retention"
          description={
            dataRetention === "enabled"
              ? "Search queries and screening results are being stored for your company."
              : "Keep a history of your team’s searches and screening results."
          }
        />
        {status === "ready" && <DataRetentionChip status={dataRetention} />}
      </div>

      {status === "error" ? (
        <Alert tone="danger" role="alert">
          Couldn’t load your data retention settings. Refresh the page to try again.
        </Alert>
      ) : (
        <>
          {saveError && !confirmDisableOpen && !pendingPeriod && (
            <Alert tone="danger" role="alert">
              {saveError}
            </Alert>
          )}

          {dataRetention === "enabled" ? (
            signature && (
              <p className="text-small text-grey-600">
                Agreed by <span className="font-medium text-grey-900">{signature.signerEmail}</span>{" "}
                on {formatDate(signature.signedAt)}.
              </p>
            )
          ) : (
            <ul className="flex flex-col gap-3">
              {DATA_RETENTION_HIGHLIGHTS.map((item) => (
                <li key={item.icon} className="flex items-start gap-3 text-small text-grey-900">
                  <Icon name={item.icon} size={20} className="mt-0.5 shrink-0 text-grey-500" />
                  <span>{item.text}</span>
                </li>
              ))}
            </ul>
          )}

          {!canManage ? (
            <p className="text-small text-grey-600">
              Only owners and admins can change this setting.
            </p>
          ) : (
            <>
              <div className="flex flex-col gap-2 border-t border-grey-200 pt-4">
                <span className="text-tiny font-semibold uppercase tracking-wide text-grey-500">
                  Retention period
                </span>
                <RetentionPeriodPicker
                  value={period}
                  onChange={(next) => void handlePeriodChange(next)}
                  disabled={
                    saving ||
                    dataRetention === "requested" ||
                    (dataRetention === "disabled" && !signature)
                  }
                />
              </div>

              {dataRetention === "disabled" && signature && (
                <div className="flex flex-col gap-3">
                  <p className="text-small text-grey-600">
                    You already signed the{" "}
                    <Link
                      to="/data-processing-agreement"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-700 underline hover:text-green-600"
                    >
                      Data Processing Agreement
                    </Link>{" "}
                    as <span className="font-medium text-grey-900">{signature.signerEmail}</span> on{" "}
                    {formatDate(signature.signedAt)} — turning this back on doesn&apos;t require
                    signing again.
                  </p>
                  <Button
                    type="button"
                    hierarchy="primary"
                    className="self-end"
                    disabled={saving}
                    onClick={() => void handleReEnable()}
                  >
                    Enable data retention
                  </Button>
                </div>
              )}

              {dataRetention === "disabled" && !signature && (
                <div className="flex flex-col gap-3">
                  <p className="text-small text-grey-600">
                    By turning this on, you agree to sanctions.io&apos;s{" "}
                    <Link
                      to="/data-processing-agreement"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-700 underline hover:text-green-600"
                    >
                      Data Processing Agreement
                    </Link>{" "}
                    and confirm you&apos;re authorized to accept this on behalf of your company, and
                    that retention fits your data protection obligations (including GDPR where
                    relevant).
                  </p>
                  <Button
                    type="button"
                    hierarchy="primary"
                    className="self-end"
                    disabled={saving || !contractId}
                    onClick={() => setSignModalOpen(true)}
                  >
                    Enable data retention
                  </Button>
                  {!contractId && (
                    <p className="text-tiny text-red-600">
                      Couldn&apos;t find the current agreement to sign. Refresh the page to try
                      again.
                    </p>
                  )}
                </div>
              )}

              {dataRetention === "requested" && (
                <div className="flex justify-end border-t border-grey-200 pt-4">
                  <Button
                    type="button"
                    hierarchy="secondary"
                    disabled={saving}
                    onClick={() => void handleCancelRequest()}
                  >
                    Cancel request
                  </Button>
                </div>
              )}

              {dataRetention === "enabled" && (
                <div className="flex items-center justify-between gap-4 border-t border-grey-200 pt-4">
                  <p className="text-small text-grey-600">
                    Turning this off clears stored history within an hour. See the{" "}
                    <Link
                      to="/data-processing-agreement"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-700 underline hover:text-green-600"
                    >
                      Data Processing Agreement
                    </Link>{" "}
                    for details.
                  </p>
                  <Button
                    type="button"
                    hierarchy="destructive"
                    disabled={saving}
                    onClick={() => setConfirmDisableOpen(true)}
                  >
                    Disable data retention
                  </Button>
                </div>
              )}
            </>
          )}
        </>
      )}

      <ConfirmDialog
        open={confirmDisableOpen}
        onClose={() => setConfirmDisableOpen(false)}
        onConfirm={() => void handleDisable()}
        title="Disable data retention?"
        description="Stored search and screening history will be purged within one hour. You can request to enable it again at any time."
        confirmLabel="Disable data retention"
        busyLabel="Disabling…"
        busy={saving}
        error={saveError ?? undefined}
        destructive
        insetLeft={insetLeft}
      />

      <ConfirmDialog
        open={pendingPeriod !== null}
        onClose={() => setPendingPeriod(null)}
        onConfirm={() => void confirmPeriodChange()}
        title="Change retention period?"
        description="This will affect the search and screening history stored for your company. This cannot be undone."
        confirmLabel="Change period"
        busyLabel="Updating…"
        busy={saving}
        error={saveError ?? undefined}
        insetLeft={insetLeft}
      />

      <SignDpaModal
        open={signModalOpen}
        onClose={() => setSignModalOpen(false)}
        onSigned={(signed) => void handleSigned(signed)}
        signerName={signerName}
        signerEmail={signerEmail}
        insetLeft={insetLeft}
      />
    </div>
  );
}
